import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Paper,
  Alert,
  CircularProgress,
} from "@mui/material";
import ArrowForwardOutlinedIcon from "@mui/icons-material/ArrowForwardOutlined";
import AnalyticsTracker from "../GASetup/AnalyticsTracker";
import {
  cardOutline,
  fonts,
  mainButtonStyles,
  semiBoldFonts,
  textFieldStyles,
} from "../../Styles/Styles";

const ContactUs = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  const [successMessage, setSuccessMessage] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [loading, setLoading] = useState(false); // Loading state

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccessMessage(null);
    setErrorMessage(null);
    setLoading(true); // Set loading to true

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_SERVER_BASE_URL}/contact/contactform`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(formData),
        }
      );

      if (response.ok) {
        setSuccessMessage(
          "Thank you for reaching out! We’ll get back to you soon."
        );
        setFormData({ name: "", email: "", subject: "", message: "" });
      } else {
        throw new Error(
          "There was an issue submitting the form. Please try again."
        );
      }
    } catch (error) {
      setErrorMessage(error.message);
    } finally {
      setLoading(false); // Set loading to false
    }
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          padding: 4,
        }}
      >
        <Helmet>
          <title>Contact Us - Listed.ky</title>
          <meta name="description" content="Contact Us - Listed.ky" />
        </Helmet>
        <AnalyticsTracker />

        {/* Hero Section */}
        <Typography
          variant="h3"
          sx={{
            mb: 2,
            ...semiBoldFonts,
            letterSpacing: 2,
            color: "var(--primary-color)",
            textTransform: "uppercase",
            position: "relative",
            display: "inline-block",
            paddingBottom: "8px",
            "&::after": {
              content: '""',
              width: "80px",
              height: "4px",
              backgroundColor: "var(--primary-color)",
              position: "absolute",
              bottom: 0,
              left: "50%",
              transform: "translateX(-50%)",
            },
          }}
        >
          Contact Us
        </Typography>
        <Typography
          variant="body1"
          sx={{
            ...fonts,
            color: "text.secondary",
            textAlign: "center",
            maxWidth: "600px",
          }}
        >
          Have any questions? Feel free to reach out.
        </Typography>

        {/* Contact Form */}
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            maxWidth: 600,
            width: "100%",
            mt: 4,
            borderRadius: "12px",
            ...cardOutline,
          }}
        >
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              {[
                { label: "Name", name: "name", type: "text" },
                { label: "Email", name: "email", type: "email" },
                { label: "Subject", name: "subject", type: "text" },
              ].map((field, index) => (
                <Grid item xs={12} key={index}>
                  <TextField
                    label={field.label}
                    variant="outlined"
                    fullWidth
                    type={field.type}
                    name={field.name}
                    value={formData[field.name]}
                    onChange={handleInputChange}
                    required
                    sx={{ ...textFieldStyles }}
                  />
                </Grid>
              ))}

              <Grid item xs={12}>
                <TextField
                  label="Message"
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={4}
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  sx={{ ...textFieldStyles }}
                />
              </Grid>
            </Grid>

            <Button
              type="submit"
              className="main-button-style"
              disabled={loading}
              sx={{ mt: 2, ...mainButtonStyles }}
            >
              {loading ? (
                <>
                  <span>Sending...</span>
                  <CircularProgress size={20} sx={{ color: "white" }} />
                </>
              ) : (
                <>
                  <span>Send Message</span>
                  <ArrowForwardOutlinedIcon />
                </>
              )}
            </Button>
          </form>
          {successMessage && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {successMessage}
            </Alert>
          )}
          {errorMessage && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {errorMessage}
            </Alert>
          )}
        </Paper>
      </Box>
      <iframe
        title="Grand Cayman Map"
        width="100%"
        height="400"
        style={{ border: 0 }}
        loading="lazy"
        allowFullScreen
        src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d240953.62659487166!2d-81.41724069210602!3d19.330127113899735!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8f258987d79949a1%3A0x3a0bed0c18902c49!2sGrand%20Cayman!5e0!3m2!1sen!2s!4v1739621738607!5m2!1sen!2s"
      ></iframe>
    </>
  );
};

export default ContactUs;
